import type React from 'react';
import type { ActiveModelStatus } from '../../../shared/api/grpc/revitClient';
import type { SearchMatch, FindReplaceOptions } from '../../../shared/lib/utils/findReplaceUtils';
import type { ImportPreview, ImportResult } from '../../../shared/lib/utils/csvUtils';
import type {
  BulkUpdateResult,
  CellUpdateResult,
  QuickFillSyncProgress,
} from '../../../shared/services/quickFillRevitService';
import type { WorkspaceIntroCardProps } from '../../../shared/ui/common/WorkspaceIntroCard';
import type {
  QuickFillAutoFillPreview,
  QuickFillAutoFillPreviewMode,
} from '../ui/components/QuickFillAutoFillPreviewDialog';
import type { QuickFillDialogShellProps } from '../ui/QuickFillDialogShell';

export interface BuildQuickFillDialogShellPropsInputs {
  modelStatus: {
    activeModelStatus: ActiveModelStatus | null;
    isModelWriteBlocked: boolean;
    modelBlockReason: 'no-document' | 'central-inaccessible' | 'read-only' | null;
    modelStatusDialogDismissed: boolean;
    setModelStatusDialogDismissed: (value: boolean) => void;
    refreshActiveModelStatus: () => void | Promise<void>;
  };
  parameterChange: {
    parameterChangeDialog: QuickFillDialogShellProps['parameterChangeDialog'];
    handleCancelParameterChange: () => void;
    handleConfirmParameterChange: () => void;
  };
  autoFill: {
    effectiveAutoFillDialogOpen: boolean;
    autoFillPreviewOnly: boolean;
    effectiveAutoFillDialogMode: QuickFillAutoFillPreviewMode;
    effectiveAutoFillDialogPreview: QuickFillAutoFillPreview;
    autoFillPreviewDialogRef: React.RefObject<HTMLDivElement | null>;
    handleCancelAutoFill: () => void;
    handleConfirmAutoFillDialog: () => void;
  };
  keyLibrary: QuickFillDialogShellProps['keyLibrary'];
  findReplace: {
    effectiveFindReplaceOpen: boolean;
    findReplacePreviewOnly: boolean;
    findReplaceDialogRef: React.RefObject<HTMLDivElement | null>;
    clearFindReplaceFocus: () => void;
    setFindReplaceOpen: (value: boolean) => void;
    visibleProperties: Array<{ label: string }>;
    findReplaceVisibleRowIndices: number[];
    selectedRows: number[];
    handleFind: (searchTerm: string, options: FindReplaceOptions) => SearchMatch[];
    handleReplace: (
      match: SearchMatch,
      replaceValue: string,
      searchTerm: string,
      options: FindReplaceOptions
    ) => void | Promise<void>;
    handleReplaceAll: (
      searchTerm: string,
      replaceValue: string,
      options: FindReplaceOptions
    ) => number | Promise<number>;
    handleFindReplaceHighlight: (match: SearchMatch | null) => void;
  };
  csvImport: {
    csvImportOpen: boolean;
    setCSVImportOpen: (value: boolean) => void;
    handleCSVPreview: (file: File) => Promise<ImportPreview>;
    handleCSVImport: (file: File) => Promise<ImportResult>;
  };
  saveReview: {
    effectiveSaveReviewOpen: boolean;
    saveReviewPreviewOnly: boolean;
    saveReviewDialogRef: React.RefObject<HTMLDivElement | null>;
    effectiveSaveReviewWarning: string | undefined;
    effectiveSaveReviewResult: BulkUpdateResult | null;
    successRows: CellUpdateResult[];
    failedRows: CellUpdateResult[];
    setSaveResultDialog: QuickFillDialogShellProps['setSaveResultDialog'];
    onContinue: () => void;
  };
  navigationGuard: {
    pendingNavigation: (() => void) | null;
    isContinuing: boolean;
    setPendingNavigation: (value: (() => void) | null) => void;
  };
  applyProgress: {
    showQuickFillApplyProgress: boolean;
    syncProgress: QuickFillSyncProgress | null | undefined;
  };
  guide: {
    guideOpen: boolean;
    setGuideOpen: (value: boolean) => void;
    handleOpenTour: (options?: { restart?: boolean }) => void;
    quickFillGuideContent: WorkspaceIntroCardProps;
  };
}

/**
 * Pure builder for the QuickFillDialogShell props.
 *
 * Groups the model-status, dialog, tour and guard state into the shape
 * expected by the dialog shell so the page does not build it inline.
 */
export function buildQuickFillDialogShellProps(
  inputs: BuildQuickFillDialogShellPropsInputs
): QuickFillDialogShellProps {
  const {
    modelStatus,
    parameterChange,
    autoFill,
    keyLibrary,
    findReplace,
    csvImport,
    saveReview,
    navigationGuard,
    applyProgress,
    guide,
  } = inputs;

  return {
    ...modelStatus,
    ...parameterChange,
    ...autoFill,
    keyLibrary,
    ...findReplace,
    ...csvImport,
    ...saveReview,
    ...navigationGuard,
    ...applyProgress,
    ...guide,
  };
}
